import { useTranslation } from "react-i18next";
import ModalUI from "./ModalUI";
import { Box, Grid, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import ControlledSelect from "../../Controller/ControlledSelect";
import { IExporterInven, ISelectOption } from "../../../types/global";
import ControlledDatePicker from "../../Controller/ControlledDatePicker";
import RadioBox from "../RadioBox";
import CustomizedButton from "../../Custom/CustomizedButton";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { InventoryExportType } from "../../../generated/inventory";

type Props = {
  open: boolean;
  closeModalHandler: () => void;
  exportHandler: (data: IExporterInven) => void;
};

const exporterSchena: IExporterInven = {
  item_type: InventoryExportType.ItemNormal,
  type: InventoryExportType.ItemNormal,
  export_type: "all",
  start_date: null,
  end_date: null,
};

const exporterValidation = Yup.object().shape({
  type: Yup.string().required("กรุณาเลือกประเภทข้อมูล"),
  start_date: Yup.date().when("export_type", {
    is: "date_range",
    then: (schema) => schema.required("กรุณาเลือกวันที่"),
    otherwise: (schema) => schema.nullable(),
  }),
  end_date: Yup.date().when("export_type", {
    is: "date_range",
    then: (schema) => schema.required("กรุณาเลือกวันที่"),
    otherwise: (schema) => schema.nullable(),
  }),
});

const ExporterInventoryModal = ({
  open,
  closeModalHandler,
  exportHandler,
}: Props) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = useForm<IExporterInven>({
    defaultValues: exporterSchena,
    resolver: yupResolver<any>(exporterValidation),
  });

  const normalItemFileTypeOptions: ISelectOption[] = [
    {
      label: t("inventory.items.import.file_type.index"),
      value: InventoryExportType.ItemNormal,
    },
  ];

  const variantItemFileTypeOptions: ISelectOption[] = [
    {
      label: t("inventory.items.import.file_type.index"),
      value: InventoryExportType.ItemVariant,
    },
    {
      label: t("inventory.items.import.file_type.sku"),
      value: InventoryExportType.VariantSku,
    },
  ];

  const bundleItemFileTypeOptions: ISelectOption[] = [
    {
      label: t("inventory.items.import.file_type.index"),
      value: InventoryExportType.ItemBundle,
    },
    {
      label: t("inventory.items.import.file_type.bundle_items"),
      value: InventoryExportType.BundleItem,
    },
  ];

  const serviceItemFileTypeOptions: ISelectOption[] = [
    {
      label: t("inventory.items.import.file_type.index"),
      value: InventoryExportType.Service,
    },
  ];

  const onClose = () => {
    reset(exporterSchena);
    closeModalHandler();
  };

  const watchItemType = watch("item_type");
  const watchExportType = watch("export_type");

  return (
    <ModalUI
      title={t("button.export")}
      open={open}
      handleClose={onClose}
      maxWidth={"md"}
      action={
        <Box sx={{ display: "flex", gap: 1 }}>
          <CustomizedButton
            title={t("button.cancel")}
            variant="outlined"
            onClick={onClose}
          />
          <CustomizedButton
            title={t("button.export")}
            variant="contained"
            onClick={() => {
              handleSubmit(exportHandler)();
              if (isValid) {
                onClose();
              }
            }}
          />
        </Box>
      }
    >
      <Typography color="primary.main" fontWeight={600} mb={2}>
        เลือกระบบ
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <RadioBox
            label={t("inventory.type.normal")}
            onClick={() => {
              setValue("item_type", InventoryExportType.ItemNormal);
              setValue("type", InventoryExportType.ItemNormal);
            }}
            checked={watchItemType === InventoryExportType.ItemNormal}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <RadioBox
            label={t("inventory.type.service")}
            onClick={() => {
              setValue("item_type", InventoryExportType.Service);
              setValue("type", InventoryExportType.Service);
            }}
            checked={watchItemType === InventoryExportType.Service}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <RadioBox
            label={t("inventory.type.bundle")}
            onClick={() => {
              setValue("item_type", InventoryExportType.ItemBundle);
              setValue("type", InventoryExportType.ItemBundle);
            }}
            checked={watchItemType === InventoryExportType.ItemBundle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <RadioBox
            label={t("inventory.type.variant")}
            onClick={() => {
              setValue("item_type", InventoryExportType.ItemVariant);
              setValue("type", InventoryExportType.ItemVariant);
            }}
            checked={watchItemType === InventoryExportType.ItemVariant}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <ControlledSelect
            name="type"
            control={control}
            label={"ข้อมูล"}
            error={Boolean(errors.type)}
            helperText={errors.type?.message?.toString()}
            options={
              watchItemType === InventoryExportType.ItemNormal
                ? normalItemFileTypeOptions
                : watchItemType === InventoryExportType.Service
                ? serviceItemFileTypeOptions
                : watchItemType === InventoryExportType.ItemVariant
                ? variantItemFileTypeOptions
                : bundleItemFileTypeOptions
            }
            required
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <RadioBox
            label={t("global.all")}
            onClick={() => {
              setValue("export_type", "all");
              setValue("start_date", null);
              setValue("end_date", null);
            }}
            checked={watchExportType === "all"}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <RadioBox
            label={t("global.date_range")}
            onClick={() => {
              setValue("export_type", "date_range");
            }}
            checked={watchExportType === "date_range"}
          />
        </Grid>
        {watchExportType === "date_range" && (
          <>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <ControlledDatePicker
                control={control}
                name="start_date"
                label={t("date.from")}
                required
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <ControlledDatePicker
                control={control}
                name="end_date"
                label={t("date.to")}
                required
              />
            </Grid>
          </>
        )}
      </Grid>
    </ModalUI>
  );
};

export default ExporterInventoryModal;
